import React, { useState } from "react";
import { Box, TextField, Divider } from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import Header from "../../../components/Header";
import { useAlert } from "../../../global/AlertContext";
import { useNavigate } from "react-router-dom";
import { useGetProductsQuery } from "../../../services/productService";
import ProductsList from "../../../components/ProductsList";
import SuccessButton from "../../../components/SuccessButton";
import SearchSelect from "../../../components/SearchSelect";
import CancelButton from "../../../components/CancelButton";
import { useBuyMutation } from "../../../services/inventoryService";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from 'dayjs';
import { useGetSuppliersQuery } from "../../../services/supplierService";


const CreatePurchase = () => {
  const showAlert = useAlert();
  const navigate = useNavigate();
  const [buy] = useBuyMutation();

  // State for search term
  const [productSearchTerm, setProductSearchTerm] = useState("");
  const [supplierSearchTerm, setSupplierSearchTerm] = useState("");

  // Fetch products with search term
  const { data: products } = useGetProductsQuery({
    name: productSearchTerm,
  });

  // Fetch suppliers with search term
  const { data: suppliers } = useGetSuppliersQuery({
    name: supplierSearchTerm,
  });

  const [productsToBuy, setProductsToBuy] = useState([]);

  const handleFormSubmit = async (values, { resetForm }) => {
    if (productsToBuy.length > 0) {
      const formattedDate = values.date ? dayjs(values.date).format("YYYY-MM-DD") : null;
      const extractedProducts = productsToBuy.map(product => ({
        product_id: product.product_id,
        quantity: product.presentation * product.quantity,
        unit_price: product.presentation_price/product.quantity
      }));

      const newPurchase = { 
        date: formattedDate, // Use the formatted date
        number: values.number,
        supplier_id: values.supplier_id, 
        products: extractedProducts 
      };
      try {
        await buy( newPurchase ).unwrap();
        showAlert("Compra creada!", "success");
        resetForm();
        navigate("/purchases");
      } catch (error) {
        console.error("Error creating purchase:", error);
        showAlert(error.message, "error");
      }
    } else {
      showAlert("Debe agregar al menos un producto", "error");
    }
  };

  const initialValues = {
    date: dayjs(),
    supplier_id: "",
    number: "",
    product_id: "",
    quantity: 1, 
    presentation: 1,
    presentation_price: 1,
  };

  const purchaseSchema = yup.object().shape({
    date: yup
          .date()
          .typeError("Ingrese una fecha válida")
          .required("Requerido"),
    number: yup.string().required("Requerido"),
    supplier_id: yup.string().required("Requerido"),
  });

  const handleAddProduct = (values, { resetForm }) => {
    // Validate product and quantity before adding a product
    if (!values.product_id) {
      showAlert("Debe seleccionar un producto", "error");
      return;
    }
    if (values.quantity < 0) {
      showAlert("La cantidad debe ser mayor a 0", "error");
      return;
    }
    if (values.presentation < 0) {
      showAlert("La presentación debe ser mayor a 0", "error");
      return;
    }
    if (values.presentation_price < 0) {
      showAlert("El precio por presentación debe ser mayor a 0", "error");
      return;
    }

    // Check if the product is already in the productsToBuy list
    const isProductAlreadyAdded = productsToBuy.some((product) => product.product_id === values.product_id);
    if (isProductAlreadyAdded) {
      showAlert("Este producto ya ha sido agregado", "error");
      return;
    }

    const product = products?.records?.find((p) => p.id === values.product_id);
    console.log(product)
    if (product) {
      const newProduct = {
        purchase_id: 0, // Will be updated when the purchase is created
        product_id: values.product_id,
        quantity: values.quantity,
        presentation: values.presentation,
        presentation_price: values.presentation_price,
        name: product.name,
        product_UOM: product.UOM, // Assuming the product has a UOM field
      };
      console.log(productsToBuy)
      setProductsToBuy([...productsToBuy, newProduct]);

      // Clear the product field after adding a product
      resetForm({
        values: {
          ...values,
          product_id: "",
          quantity: 1,
          presentation: 1,
          presentation_price: 1,
        },
      });
      setProductSearchTerm(""); // Clear the search term
    }
  };

  return (
    <Box m="20px">
      <Header title="Compras" subtitle="Crear Compra" />
      <Formik
        onSubmit={(values, { resetForm }) => handleFormSubmit(values, { resetForm })}
        initialValues={initialValues}
        validationSchema={purchaseSchema}
        validateOnChange={false} // Only validate on submit
        validateOnBlur={false}
      >
        {({ values, errors, touched, handleBlur, handleChange, handleSubmit, setFieldValue, resetForm }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="30px"
              gridTemplateColumns="repeat(3, minmax(0, 1fr))"
            >
              {/* Receipt Number */}
              <TextField
                fullWidth
                variant="filled"
                label="Número de Factura"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.number}
                name="number"
              />
              {/* Date Select */}
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Fecha de Compra"
                  value={values.date ? dayjs(values.date) : null} // Convert to Day.js object
                  onChange={(newValue) => setFieldValue("date", newValue)} // Set as Day.js object
                  format="DD/MM/YYYY" // Set user-friendly format
                  slotProps={{
                    textField: {
                      variant: "filled",
                      fullWidth: true,
                      error: !!touched.date && !!errors.date,
                      helperText: touched.date && errors.date,
                      sx: { gridColumn: "span 1" },
                    },
                  }}
                />
              </LocalizationProvider>

                {/* Supplier Select */}
              <SearchSelect
                label="Proveedor"
                options={suppliers?.records || []}
                value={suppliers?.records?.find((s) => s.id === values.supplier_id) || null}
                onChange={(newValue) => setFieldValue("supplier_id", newValue?.id || "")}
                onInputChange={(newInputValue) => {
                  setSupplierSearchTerm(newInputValue);
                }}
                getOptionLabel={(option) => option.name}
                error={!!touched.supplier_id && !!errors.supplier_id}
                helperText={touched.supplier_id && errors.supplier_id}
              />
              {/* Product, Quantity, and Add Button in the same row */}
              <Box sx={{ gridColumn: "span 3", display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: "30px" }}>
                <SearchSelect
                  label="Producto"
                  options={products?.records || []}
                  value={products?.records?.find((p) => p.id === values.product_id) || null}
                  onChange={(newValue) => {
                    setFieldValue("product_id", newValue?.id || "");
                  }}
                  onInputChange={(newInputValue) => {
                    setProductSearchTerm(newInputValue);
                  }}
                  getOptionLabel={(option) => option.name}
                  error={!!touched.product_id && !!errors.product_id}
                  helperText={touched.product_id && errors.product_id}
                />
                <TextField
                  fullWidth
                  variant="filled"
                  type="number"
                  label="Cantidad"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.quantity}
                  name="quantity"
                  inputProps={{ min: 1 }}
                />
                <TextField
                  fullWidth
                  variant="filled"
                  type="number"
                  label="Presentación"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.presentation}
                  name="presentation"
                  inputProps={{ min: 1 }}
                />
                <TextField
                  fullWidth
                  variant="filled"
                  type="number"
                  label="Precio por Presentación"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.presentation_price}
                  name="presentation_price"
                  inputProps={{ min: 1 }}
                />
                <Box textAlign="center">
                  <SuccessButton
                    label="Agregar Producto"
                    onClick={(e) => {
                      e.preventDefault(); // Prevent the form from submitting
                      handleAddProduct(values, { resetForm });
                    }}
                  />
                </Box>
              </Box>
            </Box>

            {/* Divider */}
            <Divider sx={{ my: "20px" }} />

            <Box mt="20px">
              <ProductsList productsList={productsToBuy} setProductsList={setProductsToBuy} />
            </Box>
            <Box
              display="flex"
              justifyContent="end"
              textAlign="center"
              gap="4px"
            >
              <CancelButton label="Cancelar" onClick={() => navigate('/purchases')}/>
              <SuccessButton label="Crear Compra" />
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default CreatePurchase;